import { EmptyState } from '@/components/ui/EmptyState';
import { ComingSoonBadge } from '@/components/common/ComingSoonBadge';
import type { CheckoutCatalogue } from '@/lib/types/billing';
import { BILLING_STRINGS } from '../constants/billing-strings';
import { LIVE_SUBSCRIPTION_STATUSES } from '../constants/subscription-status';
import { PlanSummaryCard } from './PlanSummaryCard';

const COPY = BILLING_STRINGS.PURCHASE;

export interface PlanCatalogueProps {
  catalogue: CheckoutCatalogue;
  /**
   * True while the startup already has data-room access with no live
   * subscription behind it (SR NEXT Premium): nothing can be bought then
   * (A-109), even though no plan is "current".
   */
  locked?: boolean;
  /**
   * The Issue Report is not on sale (FR-203). Its plan, if the catalogue still
   * lists it from a read made before it was retired, shows "Coming Soon"
   * instead of a buy action, and an otherwise empty catalogue says so.
   */
  issueReportComingSoon?: boolean;
  /**
   * The offer is still loading or failed to load, so the Issue Report plan, if
   * listed, shows no buy action and no "Coming Soon" either (FR-203).
   */
  issueReportOfferUnknown?: boolean;
}

/**
 * Every plan on sale, as cards to compare and select (FR-39, A-76).
 *
 * Shared by the purchase page and the free-tier Billing & Info page, so a
 * free startup sees the plans where it looks for them, without a click to
 * reach them (Sprint 7 QA), and both places show the same thing.
 *
 * While a subscription is live every card is locked: the backend refuses a
 * second purchase, and offering one would end in that refusal.
 */
export function PlanCatalogue({
  catalogue,
  locked = false,
  issueReportComingSoon = false,
  issueReportOfferUnknown = false,
}: PlanCatalogueProps) {
  if (catalogue.plans.length === 0) {
    return issueReportComingSoon ? (
      <EmptyState
        title={COPY.COMING_SOON_TITLE}
        description={COPY.COMING_SOON_BODY}
        action={<ComingSoonBadge />}
      />
    ) : (
      <EmptyState title={COPY.EMPTY_TITLE} description={COPY.EMPTY_BODY} />
    );
  }

  const live =
    catalogue.currentSubscription &&
    LIVE_SUBSCRIPTION_STATUSES.includes(catalogue.currentSubscription.status)
      ? catalogue.currentSubscription
      : null;

  // One plan reads as an offer, centred like the design; several as a choice.
  const featured = catalogue.plans.length === 1;

  return (
    <div className={featured ? 'mx-auto w-full max-w-2xl' : 'grid gap-4 md:grid-cols-2 xl:grid-cols-3'}>
      {catalogue.plans.map((plan) => (
        <PlanSummaryCard
          key={plan.id}
          plan={plan}
          // A cancelled subscription is history, not a current plan: treating
          // it as current locked a lapsed startup out of buying back the plan
          // it had (Sprint 7 QA).
          isCurrent={live?.planId === plan.id}
          disabled={Boolean(live) || locked}
          featured={featured}
          comingSoon={issueReportComingSoon && plan.dataRoomService === 'issue_report'}
          offerUnknown={issueReportOfferUnknown && plan.dataRoomService === 'issue_report'}
        />
      ))}
    </div>
  );
}
